import type { SetFilterValuesFuncParams } from "ag-grid-enterprise";
import type { FilterModel } from "~/components/grid/AgGrid";
import type { AppDispatch } from "~/store";

import { type GridRequestInput } from "~/gql/graphqlBase.generated";
import gridFilterFeedback from "~/helpers/grid/gridFilterFeedback";
import refreshColumnSetFilterValues from "~/helpers/grid/refreshColumnSetFilterValues";
import logger from "~/helpers/logger";
import { apiEndpoints } from "~/services/endpoints/apiEndpoints";

export interface SetFilterValuesOptions<V = string> {
	dispatch: AppDispatch;
	getSearchText?: () => string | undefined;
	labelCacheColId?: string;
	mapValues?: (values: unknown[]) => V[];
	transformRequest?: (request: GridRequestInput) => GridRequestInput;
}

export const buildSetValuesRequest = (
	filterModel: FilterModel | null | undefined,
	getSearchText?: () => string | undefined
): GridRequestInput => ({
	endRow: 0,
	filterModel: filterModel ?? null,
	groupKeys: [],
	rowGroupCols: [],
	searchText: getSearchText?.()?.trim() || undefined,
	sortModel: [],
	startRow: 0,
	valueCols: []
});

const toStringValues = (values: unknown[]): string[] => values.map(String);

interface SetFilterValueOption {
	id: string;
	name: string;
}

const setFilterValueNamesByColumn = new Map<string, Map<string, string>>();

const setFilterValueCacheKey = (gridId: string, colId: string): string => `${gridId}::${colId}`;

const isSetFilterValueOption = (value: unknown): value is SetFilterValueOption =>
	typeof value === "object" &&
	value !== null &&
	typeof (value as SetFilterValueOption).id === "string" &&
	typeof (value as SetFilterValueOption).name === "string";

const cacheSetFilterValueNames = (gridId: string, colId: string, values: unknown): void => {
	if (!Array.isArray(values)) return;
	const options = values.filter(isSetFilterValueOption);
	if (options.length === 0) return;
	const key = setFilterValueCacheKey(gridId, colId);
	const names = setFilterValueNamesByColumn.get(key) ?? new Map<string, string>();
	for (const { id, name } of options) names.set(id, name);
	setFilterValueNamesByColumn.set(key, names);
};

export const getSetFilterValueLabel = (gridId: string, colId: string, key: string): string | undefined =>
	setFilterValueNamesByColumn.get(setFilterValueCacheKey(gridId, colId))?.get(key);

export const getSetFilterValueName = getSetFilterValueLabel;

const createAgGridSetFilterValues =
	<V = string>(
		gridId: string,
		colId: string,
		{ dispatch, getSearchText, labelCacheColId = colId, mapValues, transformRequest }: SetFilterValuesOptions<V>
	) =>
	async (params: SetFilterValuesFuncParams<unknown, V>): Promise<void> => {
		gridFilterFeedback.setFilterLoadFailure(params.api, params.column?.getColId() ?? colId);
		let unsubscribe: (() => void) | undefined;
		try {
			const built = buildSetValuesRequest(params.api.getFilterModel() as FilterModel | null, getSearchText);
			const request = transformRequest ? transformRequest(built) : built;
			const action = dispatch(
				apiEndpoints.endpoints.AgGridSetValues.initiate({ colId, gridId, request }, { forceRefetch: true })
			);
			unsubscribe = () => action.unsubscribe?.();
			const result = await action;
			if (result.error) throw result.error;
			const raw = result.data?.agGridSetValues?.values;
			if (!Array.isArray(raw)) throw new Error("Missing grid filter values");
			const mapped = mapValues ? mapValues(raw) : (toStringValues(raw) as unknown as V[]);
			cacheSetFilterValueNames(gridId, labelCacheColId, mapped);
			params.success(mapped);
		} catch (error) {
			logger.error(`Grid filter values request failed for ${gridId}.${colId}`, error);
			let cancelled = false;
			gridFilterFeedback.setFilterLoadFailure(params.api, params.column?.getColId() ?? colId, {
				cancel: () => {
					if (cancelled) return;
					cancelled = true;
					params.success([]);
				},
				retry: () => {
					if (params.api.isDestroyed()) return;
					if (cancelled) {
						void refreshColumnSetFilterValues(params.api, params.column?.getColId() ?? colId).catch((refreshError) =>
							logger.error("Grid filter values retry failed", refreshError)
						);
					} else {
						void createAgGridSetFilterValues(gridId, colId, {
							dispatch,
							getSearchText,
							labelCacheColId,
							mapValues,
							transformRequest
						})(params);
					}
				}
			});
		} finally {
			unsubscribe?.();
		}
	};

export default createAgGridSetFilterValues;
